import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useUserIdentityContext } from './UserIdentityContext';

interface ArmillaryContextValue {
  /** The root sphere ID (center of the armillary) */
  rootSphereId: string | null;
  /** The root sphere type (e.g., 'subject', 'organization') */
  rootSphereType: string | null;
  /** IDs of all loaded orbital spheres (nearby spheres) */
  orbitalSphereIds: string[];
  /** Currently selected orbital sphere ID (if any) */
  selectedOrbitalId: string | null;
  /** Whether the user is viewing their own sphere (subject type where sphere ID = user ID) */
  isViewingSelf: boolean;
  /** Set the root sphere ID */
  setRootSphereId: (id: string | null) => void;
  /** Set the root sphere type */
  setRootSphereType: (type: string | null) => void;
  /** Set the loaded orbital sphere IDs */
  setOrbitalSphereIds: (ids: string[]) => void;
  /** Set the selected orbital ID */
  setSelectedOrbitalId: (id: string | null) => void;
}

const ArmillaryContext = createContext<ArmillaryContextValue | null>(null);

export interface ArmillaryProviderProps {
  children: React.ReactNode;
  /** Initial root sphere ID */
  initialRootSphereId?: string;
  /** Initial root sphere type (e.g., 'subject', 'organization') */
  initialRootSphereType?: string;
}

/**
 * ArmillaryProvider - Provides shared sphere/orbital state for Armillary view
 *
 * This context manages the core armillary sphere state: which sphere is centered,
 * which orbitals are loaded, and selection state. Notification/task/timeline
 * fetching is handled by ToolbarContext.
 */
export function ArmillaryProvider({
  children,
  initialRootSphereId,
  initialRootSphereType,
}: Readonly<ArmillaryProviderProps>): React.ReactElement {
  const { identityId } = useUserIdentityContext();
  const currentUserIdentityId = identityId;

  // Sphere state
  const [rootSphereId, setRootSphereId] = useState<string | null>(initialRootSphereId ?? null);
  const [rootSphereType, setRootSphereType] = useState<string | null>(initialRootSphereType ?? null);
  const [orbitalSphereIds, setOrbitalSphereIds] = useState<string[]>([]);
  const [selectedOrbitalId, setSelectedOrbitalId] = useState<string | null>(null);

  // Determine if user is viewing their own sphere (subject type where sphere ID matches user ID)
  const isViewingSelf = useMemo(() => {
    if (!rootSphereId || !currentUserIdentityId) return false;
    return rootSphereType === 'subject' && rootSphereId === currentUserIdentityId;
  }, [rootSphereId, rootSphereType, currentUserIdentityId]);

  // Handlers for sphere state
  const handleSetRootSphereId = useCallback((id: string | null) => {
    setRootSphereId(prev => {
      if (prev === id) return prev;
      return id;
    });
  }, []);

  const handleSetOrbitalSphereIds = useCallback((ids: string[]) => {
    setOrbitalSphereIds(prev => {
      if (prev.length === ids.length && prev.every((id, index) => id === ids[index])) {
        return prev;
      }
      return ids;
    });
  }, []);

  const handleSetSelectedOrbitalId = useCallback((id: string | null) => {
    setSelectedOrbitalId(prev => {
      if (prev === id) return prev;
      return id;
    });
  }, []);

  const handleSetRootSphereType = useCallback((type: string | null) => {
    setRootSphereType(prev => {
      if (prev === type) return prev;
      return type;
    });
  }, []);

  const value = useMemo<ArmillaryContextValue>(
    () => ({
      rootSphereId,
      rootSphereType,
      orbitalSphereIds,
      selectedOrbitalId,
      isViewingSelf,
      setRootSphereId: handleSetRootSphereId,
      setRootSphereType: handleSetRootSphereType,
      setOrbitalSphereIds: handleSetOrbitalSphereIds,
      setSelectedOrbitalId: handleSetSelectedOrbitalId,
    }),
    [
      rootSphereId,
      rootSphereType,
      orbitalSphereIds,
      selectedOrbitalId,
      isViewingSelf,
      handleSetRootSphereId,
      handleSetRootSphereType,
      handleSetOrbitalSphereIds,
      handleSetSelectedOrbitalId,
    ]
  );

  return <ArmillaryContext.Provider value={value}>{children}</ArmillaryContext.Provider>;
}

/**
 * useArmillaryContext - Hook to access the Armillary shared state
 *
 * @throws Error if used outside of ArmillaryProvider
 */
export function useArmillaryContext(): ArmillaryContextValue {
  const context = useContext(ArmillaryContext);
  if (!context) {
    throw new Error('useArmillaryContext must be used within an ArmillaryProvider');
  }
  return context;
}

/**
 * useArmillaryContextOrNull - Hook to access the Armillary shared state (optional)
 *
 * Returns null if used outside of ArmillaryProvider (useful for standalone usage)
 */
export function useArmillaryContextOrNull(): ArmillaryContextValue | null {
  return useContext(ArmillaryContext);
}

export default ArmillaryContext;
